import {
  Fragment,
  forwardRef,
  useCallback,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react'

import { Button } from '@wartek-id/button-v1'
import { Separator } from '@wartek-id/separator'
import { Text } from '@wartek-id/text-v1'
import { useToast } from '@wartek-id/toast'

import { useAPIUpdateState } from 'api/kinerja/useAPIKinerjaKS'
import PanelKinerja from 'app/KinerjaKSPage/components/PanelKinerja'
import useContentContext from 'app/KinerjaKSPage/hooks/useContentContext'
import useSKPKSTrackEvent from 'app/KinerjaKSPage/hooks/useSKPKSTrackEvent'
import { mapKuantitas } from 'app/KinerjaKSPage/utils/kinerja'
import {
  TableBody,
  TableHeader,
  TableHeaderItem,
  TablePelaksanaanKinerja,
  TableRow,
  TableRowItem,
} from 'app/KinerjaShared/components/TablePelaksanaanKinerja'
import {
  PanelKinerjaSourceTabId,
  type KinerjaKsPerencanaanTabBaseProps,
} from 'types/kinerja-ks'
import TabRangkumanReady from 'app/KinerjaKSPage/pages/Perencanaan/TabRangkuman/Ready'
import DialogFinalizeSKP from 'app/KinerjaKSPage/pages/Perencanaan/TabRangkuman/components/DialogFinalizeSKP'
import useDemoContext from 'app/KinerjaPage/hooks/useDemoContext'
import { useApiSyncSkpPerencanaan } from 'api/kinerja/pengaliran-data-ekin'

import type { ForwardedRef } from 'react'
import type { PerilakuKerjaPayload, SKPTabHandler } from 'types/kinerja'

interface PerencanaanTabRangkumanProps
  extends KinerjaKsPerencanaanTabBaseProps {
  onRequestChange?: () => void
  onClickBackToHome?: () => void
  onClickSave?: () => void
  hideDetails?: boolean
}

const TabRangkuman = (
  {
    skp,
    isSubmitting,
    onRequestChange = () => {},
    onClickBackToHome = () => {},
    onClickSave = () => {},
    hideDetails = false,
  }: PerencanaanTabRangkumanProps,
  ref: ForwardedRef<SKPTabHandler>
) => {
  const { trackClickProposePlanning, trackClickProposePlanningConfirmation } =
    useSKPKSTrackEvent()

  const {
    contentPengembanganKompetensi,
    contentPerilakuKerja,
    contentRencanaHasilKerjaEkstra,
    getKepemimpinanSatuanPendidikanById,
  } = useContentContext()
  const toast = useToast()
  const { isDemoMode } = useDemoContext()

  const getPerilakuKerjaRow = (value: PerilakuKerjaPayload) => {
    const aspek = contentPerilakuKerja.aspek.find(
      ({ id }) => value.aspekId === id
    )
    const indikator = aspek?.indikator.find(
      ({ id }) => id === value.indikatorId
    )

    const ekspektasi = indikator?.ekspektasi.find(
      ({ id }) => id === value.ekspektasiId
    )

    return {
      aspek: aspek?.value ?? '',
      indikator: indikator?.value ?? '',
      ekspektasi: ekspektasi?.value ?? '',
    }
  }

  const [isOpenDialogFinalize, setIsOpenDialogFinalize] = useState(false)

  const {
    mutateAsync: finalizeSKP,
    isLoading: isFinalizingSKP,
    isSuccess: isSuccessFinalize,
  } = useAPIUpdateState({ isDemoMode })

  const content = useMemo(() => {
    return [
      // Default top
      ...contentRencanaHasilKerjaEkstra
        ?.filter((v) => !!v?.position?.placeOnTop)
        ?.sort((a, b) => {
          const sortingA = isNaN(a.position.order) ? 0 : a.position.order
          const sortingB = isNaN(b.position.order) ? 0 : b.position.order
          return sortingA - sortingB
        })
        .map((v) => {
          return {
            ...v,
            sourceTabId: PanelKinerjaSourceTabId.RencanaHasilKerjaEkstra,
            label: 'Rencana Hasil Kerja',
          }
        }),

      // Praktik Kinerja Kepala Sekolah
      ...skp.kepemimpinanSatuanPendidikan.map((id) => {
        return {
          ...getKepemimpinanSatuanPendidikanById(id),
          sourceTabId: PanelKinerjaSourceTabId.PraktikKinerjaKepalaSekolah,
          label: 'Rencana Hasil Kerja',
        }
      }),

      // Pengembangan Kompetensi
      ...skp.pengembanganKompetensi
        .filter((k) => k.id > 0) // Only include the valid ones
        .map((kinerja) => {
          const selectedPengembanganKompetensi = mapKuantitas(
            contentPengembanganKompetensi,
            kinerja
          )

          return {
            ...selectedPengembanganKompetensi,
            bobot:
              selectedPengembanganKompetensi?.bobot || 0 * kinerja.kuantitas,
            sourceTabId: PanelKinerjaSourceTabId.PengembanganKompetensi,
            label: 'Indikator Kompetensi',
          }
        }),

      // Default bottom
      ...contentRencanaHasilKerjaEkstra
        ?.filter((v) => !v?.position?.placeOnTop)
        ?.sort((a, b) => {
          const sortingA = isNaN(a.position.order) ? 0 : a.position.order
          const sortingB = isNaN(b.position.order) ? 0 : b.position.order
          return sortingA - sortingB
        })
        .map((v) => {
          return {
            ...v,
            sourceTabId: PanelKinerjaSourceTabId.RencanaHasilKerjaEkstra,
            label: 'Rencana Hasil Kerja',
          }
        }),
    ]
  }, [
    contentPengembanganKompetensi,
    contentRencanaHasilKerjaEkstra,
    getKepemimpinanSatuanPendidikanById,
    skp.kepemimpinanSatuanPendidikan,
    skp.pengembanganKompetensi,
  ])

  const { mutateAsync: syncSkpToBkn } = useApiSyncSkpPerencanaan()
  const handleClickFinalize = async () => {
    trackClickProposePlanning({
      kinerjaPlanningId: skp.id,
      kinerjaPlanningStatus: skp.status,
      screenName: 'KINERJA_PLANNING_KS_PENGAJUAN_POP_UP',
    })

    try {
      await finalizeSKP({
        skpId: skp.id,
      })

      if (skp.ekinEligible) {
        // Due to complexity in the BE side, we need to sync SKP to BKN
        // manually after finalizing the SKP instead of automatically
        // syncing it in the BE side. We ignore the result of this
        // operation since it is not critical to the user experience.
        await syncSkpToBkn({
          year: skp.tahun,
          force: 0,
        }).catch((e) => {
          console.error('Failed to sync SKP to BKN:', e)
        })
      }

      setIsOpenDialogFinalize(false)
    } catch (e) {
      console.error(e)

      toast({
        message: 'Gagal dikumpulkan. Silakan coba lagi.',
      })
    }
  }

  const onSaveDraft = useCallback(() => {
    onClickSave()
  }, [onClickSave])

  useImperativeHandle(
    ref,
    () => ({
      saveDraft: onSaveDraft,
    }),
    [onSaveDraft]
  )

  if (isSuccessFinalize) {
    return <TabRangkumanReady onClickBackToHome={onClickBackToHome} />
  }

  return (
    <div>
      <div className="py-9 px-6">
        <div className="flex flex-col gap-1">
          <Text variant="heading-md">Rangkuman Perencanaan Kinerja Anda</Text>
          <Text color="subdued">
            Format Perencanaan Kinerja akan kami sesuaikan dengan aturan yang
            berlaku. Sehingga, terdapat perencanaan yang otomatis ditambahkan
            selain yang Anda pilih. Mohon dicek, ya.
          </Text>
        </div>

        <section className="mt-9">
          <Text as="h2" variant="heading-sm" className="mb-4">
            Hasil Kerja Utama
          </Text>

          <div className="flex flex-col gap-6">
            {content.map((c, index) => {
              return (
                <Fragment key={index}>
                  <PanelKinerja
                    hideDetails={hideDetails}
                    label={c.label}
                    kinerja={c}
                    number={index + 1}
                  />
                </Fragment>
              )
            })}
          </div>
        </section>

        <section className="mt-9">
          <Text as="h2" variant="heading-sm">
            Perilaku Kerja
          </Text>

          <TablePelaksanaanKinerja>
            <TableHeader>
              <TableHeaderItem>No.</TableHeaderItem>
              <TableHeaderItem>Aspek</TableHeaderItem>
              <TableHeaderItem>Indikator</TableHeaderItem>
              <TableHeaderItem>Perwujudan</TableHeaderItem>
            </TableHeader>
            <TableBody>
              {skp?.perilakuKerja
                ?.map(getPerilakuKerjaRow)
                ?.map((perilakuKerja, idx) => (
                  <TableRow key={`perilaku-kerja-${idx}`}>
                    <TableRowItem>{idx + 1}</TableRowItem>
                    <TableRowItem>{perilakuKerja.aspek}</TableRowItem>
                    <TableRowItem>{perilakuKerja.indikator}</TableRowItem>
                    <TableRowItem>{perilakuKerja.ekspektasi}</TableRowItem>
                  </TableRow>
                ))}
            </TableBody>
          </TablePelaksanaanKinerja>
        </section>
      </div>

      <Separator />

      <div className="px-8 py-4 flex gap-5 justify-end">
        <Button size="lg" color="white" onClick={onRequestChange}>
          Ubah
        </Button>
        <Button
          size="lg"
          isLoading={isSubmitting}
          disabled={isSubmitting}
          type="submit"
          data-testid="btn-next"
          onClick={() => {
            trackClickProposePlanningConfirmation({
              kinerjaPlanningId: skp.id,
              kinerjaPlanningStatus: skp.status,
              screenName: 'KINERJA_PLANNING_KS_RANGKUMAN_PAGE',
            })

            setIsOpenDialogFinalize(true)
          }}
        >
          Kumpulkan
        </Button>

        <DialogFinalizeSKP
          data-testid="dialog-finalize-skp"
          isOpen={isOpenDialogFinalize}
          onClose={() => setIsOpenDialogFinalize(false)}
          isFinalizing={isFinalizingSKP}
          onClickCancel={() => setIsOpenDialogFinalize(false)}
          onClickFinalize={handleClickFinalize}
          ekinEligible={skp.ekinEligible}
        />
      </div>
    </div>
  )
}

export default forwardRef<SKPTabHandler, PerencanaanTabRangkumanProps>(
  TabRangkuman
)
